import type { MutableRefObject } from 'react';
import React, { useEffect, useState } from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { Drawer } from '@noths/polaris-client-ribbons-design-system';

import { BasketCTAButtonContainer } from 'src/components/molecules/BasketCTAButton/BasketCTAButtonContainer';
import * as copy from 'src/components/organisms/Personalisation/constants/copy';
import { PersonalisationContainer } from 'src/components/organisms/Personalisation/containers/PersonalisationContainer';
import type { MediaItem } from 'src/components/organisms/ProductMediaCarousel/types';
import { ProductOptionsDrawerImages } from './ProductOptionsDrawerImages';
import * as styles from './ProductOptionsDrawer.styles';

export interface ProductOptionsDrawerStateProps {
  heading: string | null;
  isOpen: boolean;
  media: MediaItem[];
  showValidationErrors: boolean;
}

export interface ProductOptionsDrawerDispatchProps {
  onClose: () => void;
  onVisible: () => void;
}

export interface ProductOptionsDrawerOwnProps {
  target: MutableRefObject<HTMLDivElement | null>;
}

type ProductOptionsDrawerProps = ProductOptionsDrawerStateProps &
  ProductOptionsDrawerDispatchProps &
  ProductOptionsDrawerOwnProps;

const isMobileDevice = () => {
  return /Mobi|Android/i.test(navigator.userAgent);
};

export const ProductOptionsDrawer = ({
  heading,
  isOpen,
  media,
  onClose,
  onVisible,
  showValidationErrors,
  target,
}: ProductOptionsDrawerProps) => {
  const [width, setWidth] = useState(0);
  const contentRef = useImpressionRef<HTMLDivElement>(() => onVisible());

  useEffect(() => {
    const updateWidth = () => {
      const leftPos = target.current?.getBoundingClientRect().left;
      const currentWidth = isMobileDevice()
        ? window.screen.width - leftPos!
        : window.innerWidth - leftPos!;
      setWidth(currentWidth);
    };

    updateWidth();

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Drawer contentLabel={copy.CONTENT_LABEL} isOpen={isOpen} onRequestClose={onClose}>
      <section css={styles.section} style={{ width }}>
        <header css={styles.header}>
          <button
            aria-expanded={isOpen}
            aria-label="Close"
            css={styles.closeButton}
            onClick={onClose}
          />
        </header>
        <div css={styles.content} ref={contentRef}>
          <ProductOptionsDrawerImages media={media} />
          {heading && <h2 css={styles.heading}>{heading}</h2>}
          <form noValidate>
            <PersonalisationContainer displayValidation={showValidationErrors} />
          </form>
        </div>
        <div css={styles.footer}>
          <BasketCTAButtonContainer isDrawerCta />
        </div>
      </section>
    </Drawer>
  );
};
